import { find } from 'lodash-es'
import { AssemblyKind, LayerTypeEnum } from 'src/constants/assemblies'

const getTimberFrameLayer = (assembly: Assembly) => {
  const timberFrameLayer = find(assembly.layers, layer =>
    [
      LayerTypeEnum.TimberFrameLayer,
      LayerTypeEnum.TimberFrameLayerWall,
      LayerTypeEnum.TimberFrameLayerRoofCeiling,
    ].includes(layer.kind as LayerType),
  )
  if (!timberFrameLayer) {
    throw new Error('Could not find timber frame layer for assembly.')
  }
  return timberFrameLayer as TimberFrameLayer
}

export const getCltLayer = (assembly: Assembly) => {
  const cltLayer = find(assembly.layers, ['kind', LayerTypeEnum.CLTLayer])
  if (!cltLayer) {
    throw new Error(`Could not find clt layer for assembly.`)
  }
  return cltLayer as CLTAssemblyLayer
}

export const getSlabBeamCrossSectionFromAssembly = (
  assembly: Assembly,
): CrossSection | CLTCrossSection => {
  if (
    assembly.kind === AssemblyKind.TimberFrame ||
    assembly.kind === AssemblyKind.TimberFrameWall ||
    assembly.kind === AssemblyKind.TimberFrameRoof ||
    assembly.kind === AssemblyKind.TimberFrameCeiling
  ) {
    const timberFrameLayer = getTimberFrameLayer(assembly)
    const slabBeamCrossSection =
      'standard_beam_cs' in timberFrameLayer
        ? timberFrameLayer.standard_beam_cs
        : timberFrameLayer.standard_rip_cs
    return slabBeamCrossSection
  } else if (
    assembly.kind === AssemblyKind.CLT ||
    assembly.kind === AssemblyKind.CLTRoof ||
    assembly.kind === AssemblyKind.CLTCeiling
  ) {
    const cltLayer = getCltLayer(assembly)
    const slabBeamCrossSection = cltLayer.clt
    return slabBeamCrossSection
  }
  throw new Error(`Unexpected assembly kind: ${assembly.kind}`)
}

export const getSlabBeamStepSizeFromAssembly = (assembly: Assembly) => {
  if (
    assembly.kind === AssemblyKind.TimberFrame ||
    assembly.kind === AssemblyKind.TimberFrameWall ||
    assembly.kind === AssemblyKind.TimberFrameRoof ||
    assembly.kind === AssemblyKind.TimberFrameCeiling
  ) {
    const timberFrameLayer = getTimberFrameLayer(assembly)
    const stepSize = timberFrameLayer.rip_step_size
    return stepSize
  } else if (
    assembly.kind === AssemblyKind.CLT ||
    assembly.kind === AssemblyKind.CLTRoof ||
    assembly.kind === AssemblyKind.CLTCeiling
  ) {
    const cltLayer = getCltLayer(assembly)
    const stepSize = cltLayer.clt.width
    return stepSize
  }
  throw new Error(`Unexpected assembly kind: ${assembly.kind}`)
}

const isMissingShortName = (shortName: string | undefined) => !shortName?.trim()

// Mirrors the backend policy (md_core.model.structural.assemblies.short_names):
// every assembly of a library has its own short name; a new one gets
// <prefix><count + 1>, a taken name gets " (1)", " (2)", ... like a copied file.
const SHORT_NAME_MAX_LENGTH = 25
const MAX_SHORT_NAME_SUFFIX = 99

export const SHORT_NAME_PREFIX_BY_USAGE_TYPE: Record<AssemblyTypes, string> = {
  Inner: 'IW',
  Outer: 'AW',
  Ceiling: 'DE',
  Roof: 'DA',
}

export const getAvailableShortName = (candidate: string, assemblies: Assembly[]): string => {
  const taken = new Set(assemblies.map(assembly => assembly.short_name))
  if (!taken.has(candidate)) {
    return candidate
  }
  for (let number = 1; number <= MAX_SHORT_NAME_SUFFIX; number++) {
    const suffix = ` (${number})`
    const suffixed = `${candidate.slice(0, SHORT_NAME_MAX_LENGTH - suffix.length)}${suffix}`
    if (!taken.has(suffixed)) {
      return suffixed
    }
  }
  // Out of suffixes: keep the candidate, the backend rejects it (MD-CORE-0020).
  return candidate
}

/** The short name a new assembly of `usageType` is proposed with. */
export const getDefaultShortName = (usageType: AssemblyTypes, assemblies: Assembly[]): string => {
  const count = assemblies.filter(assembly => assembly.usage_type === usageType).length
  const number = String(count + 1).padStart(2, '0')
  return getAvailableShortName(`${SHORT_NAME_PREFIX_BY_USAGE_TYPE[usageType]}${number}`, assemblies)
}

/** The short name a copy of `assembly` is proposed with. */
export const getShortNameForCopy = (assembly: Assembly, assemblies: Assembly[]): string =>
  isMissingShortName(assembly.short_name)
    ? getDefaultShortName(assembly.usage_type, assemblies)
    : getAvailableShortName(assembly.short_name, assemblies)
